'use client';

import { useEffect, useRef, useState } from 'react';

/** Порог (px): если sentinel ближе к верху viewport — считаем «у начала списка» и показываем фильтры */
const SENTINEL_NEAR_TOP_THRESHOLD = 20;

/**
 * Хук для управления видимостью sticky элемента
 *
 * Показывает sticky элемент когда:
 * - Root элемент не полностью видим в viewport
 * - И происходит скролл вверх
 * - ИЛИ sentinel у верхнего края viewport (подскролл к началу списка)
 *
 * Скрывает sticky элемент когда:
 * - Происходит скролл вниз
 * - ИЛИ root элемент полностью видим в viewport
 * - НО НЕ скрывает если недавно было взаимодействие с фильтрами
 */
export function useStickyVisibility() {
  const [isRootAboveViewport, setIsRootAboveViewport] = useState(false);
  const [sentinelTop, setSentinelTop] = useState(0);
  const [isScrollingUp, setIsScrollingUp] = useState(false);
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const [showSticky, setShowSticky] = useState(false);
  const [isInteractionBlocked, setIsInteractionBlocked] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const lastScrollY = useRef(0);
  const scrollDirection = useRef<'up' | 'down' | null>(null);
  const scrollDebounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const interactionTimeout = useRef<NodeJS.Timeout | null>(null);
  const rafRef = useRef<number | null>(null);

  // Функция для вызова при взаимодействии с фильтрами
  const onInteraction = () => {
    setIsInteractionBlocked(true);

    // Сбрасываем предыдущий таймер
    if (interactionTimeout.current) {
      clearTimeout(interactionTimeout.current);
    }

    // Снимаем блокировку через 2 секунды после последнего взаимодействия
    interactionTimeout.current = setTimeout(() => {
      setIsInteractionBlocked(false);
    }, 2000);
  };

  // Отслеживание направления скролла с оптимизацией через RAF
  useEffect(() => {
    const handleScroll = () => {
      // Отменяем предыдущий RAF если есть
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;
        const diff = Math.abs(currentScrollY - lastScrollY.current);

        const threshold = 3;

        if (diff >= threshold) {
          const newDirection =
            currentScrollY < lastScrollY.current ? 'up' : 'down';

          if (newDirection !== scrollDirection.current) {
            scrollDirection.current = newDirection;
            setIsScrollingUp(newDirection === 'up');
            setIsScrollingDown(newDirection === 'down');
          }

          lastScrollY.current = currentScrollY;

          // Сбрасываем направление скролла
          if (scrollDebounceTimeout.current) {
            clearTimeout(scrollDebounceTimeout.current);
          }

          scrollDebounceTimeout.current = setTimeout(() => {
            scrollDirection.current = null;
            setIsScrollingUp(false);
            setIsScrollingDown(false);
          }, 500);
        }
      });
    };

    lastScrollY.current = window.scrollY;
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollDebounceTimeout.current) {
        clearTimeout(scrollDebounceTimeout.current);
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  // Отслеживание позиции root элемента
  useEffect(() => {
    const currentRoot = rootRef.current;
    if (!currentRoot) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const top = entry.boundingClientRect.top;
        setSentinelTop(top);
        // Элемент считается "выше viewport" когда он не полностью видим (intersectionRatio < 1)
        // и его верхняя граница находится выше экрана
        const isAbove = entry.intersectionRatio < 1 && top < 0;

        setIsRootAboveViewport(isAbove);
      },
      {
        threshold: 1.0, // Весь элемент должен быть в viewport
        rootMargin: '0px 0px 0px 0px'
      }
    );

    observer.observe(currentRoot);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Определяем когда показывать sticky элемент
  useEffect(() => {
    // Показываем элемент если root НЕ выше viewport (элемент виден всегда до rootRef)
    if (!isRootAboveViewport && !showSticky) {
      setShowSticky(true);
    }

    // После достижения rootRef (когда он выше viewport) работает логика скролла
    if (isRootAboveViewport) {
      const isNearTop = sentinelTop > -SENTINEL_NEAR_TOP_THRESHOLD;
      if ((isScrollingUp || isNearTop) && !showSticky) {
        setShowSticky(true);
      }

      /** При перезагрузке со скроллом sentinel уже выше viewport — скрываем sticky до скролла вверх */
      if (!isNearTop && !isScrollingUp && showSticky && !isInteractionBlocked) {
        setShowSticky(false);
      }

      if (
        !isNearTop &&
        isScrollingDown &&
        showSticky &&
        !isInteractionBlocked
      ) {
        setShowSticky(false);
        const timeoutId = setTimeout(() => {
          setShowSticky(false);
        }, 100);

        return () => clearTimeout(timeoutId);
      }
    }
  }, [
    isRootAboveViewport,
    sentinelTop,
    isScrollingUp,
    isScrollingDown,
    showSticky,
    isInteractionBlocked
  ]);

  // Очистка таймеров при размонтировании
  useEffect(() => {
    return () => {
      if (interactionTimeout.current) {
        clearTimeout(interactionTimeout.current);
      }
    };
  }, []);

  return {
    rootRef,
    showSticky,
    onInteraction,
    isRootAboveViewport,
    isScrollingUp,
    isScrollingDown,
    isInteractionBlocked
  };
}
